<script lang="ts">
    import Modal        from '../components/Modal.svelte';
    import ConfirmModal from '../components/ConfirmModal.svelte';
    import AlertModal   from '../components/AlertModal.svelte';
    import Button       from '../components/forms/Button.svelte';
    import { addToast } from '../stores/toast';

    // Base modal
    let baseOpen = false;

    // Confirm modals
    let confirmOpen      = false;
    let confirmDestrOpen = false;
    let confirmResult    = '';

    // Alert modals
    let alertInfo    = false;
    let alertSuccess = false;
    let alertWarning = false;
    let alertError   = false;
</script>

<div class="page">
    <h1>Modals</h1>

    <!-- ── Base modal ──────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Base Modal</h2>
        <div class="card-body">
            <p class="section-desc">Generic modal with a title, scrollable body and a footer slot. Use this as a building block for any custom dialog.</p>
            <div class="demo-row">
                <Button variant="primary" on:click={() => baseOpen = true}>Open Modal</Button>
            </div>
        </div>
    </section>

    <!-- ── Confirm modal ───────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Confirm Modal</h2>
        <div class="card-body">
            <p class="section-desc">Asks the user to confirm or cancel an action. Use <code>destructive</code> for irreversible actions.</p>
            <div class="demo-row">
                <Button variant="primary" on:click={() => confirmOpen = true}>Confirm action</Button>
                <Button variant="danger"  on:click={() => confirmDestrOpen = true}>Delete item</Button>
            </div>
            {#if confirmResult}
                <p class="result">Result: <strong>{confirmResult}</strong></p>
            {/if}
        </div>
    </section>

    <!-- ── Alert modal ─────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Alert Modal</h2>
        <div class="card-body">
            <p class="section-desc">Informs the user with a single OK button. Available in four severity levels.</p>
            <div class="demo-row">
                <Button variant="default"   on:click={() => alertInfo    = true}>Info</Button>
                <Button variant="success" on:click={() => alertSuccess = true}>Success</Button>
                <Button variant="warning" on:click={() => alertWarning = true}>Warning</Button>
                <Button variant="danger"  on:click={() => alertError   = true}>Error</Button>
            </div>
        </div>
    </section>

    <!-- ── Toasts ───────────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Toasts</h2>
        <div class="card-body">
            <p class="section-desc">
                Non-blocking notifications that auto-dismiss (except <code>error</code> which is sticky).
                Import <code>addToast(message, type, options)</code> from the toast store.
            </p>
            <div class="demo-row">
                <Button variant="primary" on:click={() => addToast('New logs received from device.', 'info', { title: 'New Logs' })}>Info</Button>
                <Button variant="success" on:click={() => addToast('Firmware update completed successfully.', 'success', { title: 'Update Done' })}>Success</Button>
                <Button variant="warning" on:click={() => addToast('CPU temperature is above threshold.', 'warning', { title: 'Warning' })}>Warning</Button>
                <Button variant="danger"  on:click={() => addToast('Connection to device lost.', 'error', { title: 'Error' })}>Error (sticky)</Button>
                <Button on:click={() => addToast('Background job is running...', 'info')}>No title</Button>
            </div>
        </div>
    </section>
</div>

<Modal bind:open={baseOpen} title="Example Modal" size="md">
    <p>This is the base modal. Drop any content into the default slot and optionally provide a <code>footer</code> slot for actions.</p>
    <p>It supports <strong>Escape</strong> to close, clicking the backdrop to dismiss, and prevents body scrolling while open.</p>

    <svelte:fragment slot="footer">
        <Button variant="default"   on:click={() => baseOpen = false}>Cancel</Button>
        <Button variant="primary" on:click={() => baseOpen = false}>Save</Button>
    </svelte:fragment>
</Modal>

<ConfirmModal
    bind:open={confirmOpen}
    title="Confirm Action"
    message="Are you sure you want to proceed? This will apply the selected changes."
    confirmLabel="Confirm"
    cancelLabel="Cancel"
    on:confirm={() => confirmResult = 'confirmed'}
    on:cancel={()  => confirmResult = 'cancelled'}
/>

<ConfirmModal
    bind:open={confirmDestrOpen}
    title="Delete Item"
    message="This item will be permanently deleted and cannot be recovered."
    confirmLabel="Delete"
    cancelLabel="Cancel"
    destructive={true}
    on:confirm={() => confirmResult = 'deleted'}
    on:cancel={()  => confirmResult = 'cancelled'}
/>

<AlertModal bind:open={alertInfo}    type="info"    title="Information" message="The configuration has been synced with the device." />
<AlertModal bind:open={alertSuccess} type="success" title="Success"     message="Firmware update completed successfully." />
<AlertModal bind:open={alertWarning} type="warning" title="Warning"     message="Sensor 0x2F has not responded for 5 minutes." />
<AlertModal bind:open={alertError}   type="error"   title="Error"       message="Connection to device lost. Please check the network." />

<style lang="scss">
    .section-desc {
        color: $color-text-secondary;
        font-size: 0.875em;
        line-height: 1.5;
    }

    code {
        font-family: $font-family-mono;
        font-size: 0.9em;
        background: $color-bg-active;
        padding: 0.1em 0.35em;
        border-radius: 3px;
    }

    .demo-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
    }

    .result {
        font-size: 0.875em;
        color: $color-text-secondary;
    }
</style>
